import { forwardRef, RefObject, useMemo, useRef, useState } from 'react';

import {
  Badge,
  Box,
  Button,
  Checkbox,
  Divider,
  Heading,
  HStack,
  List,
  ListItem,
  Portal,
  Stack,
  Text,
} from '@chakra-ui/react';

import {
  CezanneOrgUnit,
  CezannePaySchedule,
  CreateCompanyWithConnectionRequest,
  CreateExternalPayCalendarRequest,
  CreateGroupPathRequest,
  NonBusinessDayFailover,
  PayrollCutOff,
} from '@aslanpay/portal-common-lib';

import useCreateCompaniesWithConnections from 'app/hooks/useCreateCompaniesWithConnections';
import useCreateExternalPayCalendars from 'app/hooks/useCreateExternalPayCalendars';
import useResolveGroupPaths, {
  ResolveGroupPathsForCompanyRequest,
} from 'app/hooks/useResolveGroupPaths';

import { CezanneSyncReviewProps, PayCalendarRequestMeta } from './types';

// Cezanne's sync data has no signal for these - they're pure Aslan-side configuration choices with no
// equivalent in the source system, so every calendar this wizard creates uses the same UK-standard defaults.
// Correctable afterwards per company via the existing pay-calendar edit UI if wrong for a given company.
const DEFAULT_WORKING_DAYS: CreateExternalPayCalendarRequest['workingDays'] = [
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
];

const CUT_OFF_TYPES: Record<string, PayrollCutOff> = {
  'Fixed day': 'FIXED_DAY',
  'Nth business day of the period': 'N_BUSINESS_DAY_OF_PERIOD',
  'Nth day of the period': 'N_DAY_OF_PERIOD',
  'Number of business days before pay day': 'N_BUSINESS_DAYS_BEFORE_PAYDAY',
  'Number of days before pay day': 'N_DAYS_BEFORE_PAYDAY',
};

const CUT_OFF_ACTIONS: Record<string, NonBusinessDayFailover> = {
  'No change': 'NO_CHANGE',
  'Default to previous business day': 'PREVIOUS_BUSINESS_DAY',
  'Default to next business day': 'NEXT_BUSINESS_DAY',
};

type CezanneCutoff = Pick<
  CreateExternalPayCalendarRequest,
  'payrollCutoff' | 'payrollCutoffDays' | 'cutoffNonBusinessDayFailover'
>;

/**
 * Maps a Cezanne schedule's `aslanCutOffValue`/`aslanCutOffType`/`aslanCutOffAction` to the pay calendar
 * request's cutoff fields, or `undefined` when the schedule carries no recognisable cutoff rule
 * (blank/unmapped type or action, or a missing or null value) - calendar-manager's own default then applies, same as before
 * this schedule carried any cutoff data. Mirrors third-party-gateway's `CezanneCutoffMapper` - the string
 * values are the Cezanne admin-UI dropdown labels, not yet byte-verified against the raw OData wire value.
 */
const resolveCutoff = (schedule: CezannePaySchedule): CezanneCutoff | undefined => {
  const type = schedule.aslanCutOffType ? CUT_OFF_TYPES[schedule.aslanCutOffType] : undefined;
  const action = schedule.aslanCutOffAction
    ? CUT_OFF_ACTIONS[schedule.aslanCutOffAction]
    : undefined;

  // typeof, not `=== undefined`: Cezanne can send an explicit null, which calendar-manager now rejects with a 400
  // instead of falling back to the default.
  if (typeof schedule.aslanCutOffValue !== 'number' || !type || !action) {
    return undefined;
  }

  return {
    payrollCutoff: type,
    payrollCutoffDays: schedule.aslanCutOffValue,
    cutoffNonBusinessDayFailover: action,
  };
};

/**
 * Derives a root-to-leaf group name path for every org unit, walking `parentCode` up to the root (a
 * single-element path when an org unit has no parent). Cezanne's tree has no depth limit signal, so a `code`
 * already seen on the current walk stops it rather than looping forever on a corrupt/cyclic parentCode chain.
 * Each org unit becomes its own request row, correlated back to it via `id` - intermediate ancestors get
 * redundantly re-resolved across sibling paths, which is fine since group-path resolution is idempotent.
 */
const buildOrgUnitGroupPaths = (orgUnits: CezanneOrgUnit[]): CreateGroupPathRequest[] => {
  const byCode = new Map(orgUnits.map((orgUnit) => [orgUnit.code, orgUnit]));

  return orgUnits.map((orgUnit) => {
    const path: string[] = [];
    const visited = new Set<string>();
    let current: CezanneOrgUnit | undefined = orgUnit;

    while (current && !visited.has(current.code)) {
      visited.add(current.code);
      path.unshift(current.name ?? current.code);
      current = current.parentCode ? byCode.get(current.parentCode) : undefined;
    }

    return { id: orgUnit.code, path };
  });
};

/** Step 2 of the Connect-Cezanne wizard: shows what the (synchronous, already-completed) sync found per
 * ASD-4330's acceptance criteria - company details, pay schedules, org-unit tree, and per-company readiness.
 * Confirm builds a CreateCompanyWithConnectionRequest per selected company (using the credentials held from
 * step 1) and calls company-manager's generic bulk-onboarding endpoint - see ASD-4330's ownership overhaul.
 * On success, two independent follow-ups run per successfully-created company, neither of which can flip that
 * company's own row to "Failed" since the company itself was already created and is not rolled back:
 * one EXTERNAL pay calendar per pay schedule from the sync's real period dates (ASD-4346), and the org-unit
 * tree resolved/created as groups via company-manager's group-path-batch endpoint (ASD-4369). */
const CezanneSyncReview = forwardRef<HTMLElement, CezanneSyncReviewProps>(
  ({ result, credentials, onStartOver, onConfirmed }, ref) => {
    const {
      mutate: confirmSync,
      data: confirmResult,
      isLoading: isConfirming,
      isError: isConfirmError,
    } = useCreateCompaniesWithConnections();
    const {
      mutate: createExternalPayCalendars,
      data: payCalendarsResult,
      isLoading: isCreatingPayCalendars,
      isError: isCreatePayCalendarsError,
    } = useCreateExternalPayCalendars();
    const {
      mutate: resolveGroupPaths,
      data: groupPathsResult,
      isLoading: isResolvingGroupPaths,
    } = useResolveGroupPaths();
    // Parallel to the requests sent to createExternalPayCalendars - lets the (order-preserving) bulk response
    // be zipped back to the company it belongs to for display, without a synthetic composite id. A ref, not
    // state, since it only needs to be read during render (triggered by payCalendarsResult changing) - never
    // needs to trigger a render of its own, and updates synchronously with no risk of the mutation's response
    // arriving before a batched state update has flushed.
    const payCalendarRequestMetaRef = useRef<PayCalendarRequestMeta[]>([]);
    // null means "not yet customised" - defaults to every ready company until the user touches a checkbox.
    const [selectedCompanyCodes, setSelectedCompanyCodes] = useState<Set<string> | null>(null);

    const { companies, paySchedules, orgUnits } = result;
    // Same org-unit tree applied to every created company, same as paySchedules above - computed once since
    // it doesn't depend on which companies actually got created.
    const orgUnitGroupPaths = useMemo(() => buildOrgUnitGroupPaths(orgUnits), [orgUnits]);
    const readyCompanyCodes = companies
      .filter((company) => company.missingMandatoryFields.length === 0)
      .map((company) => company.companyCode)
      .filter((code): code is string => !!code);
    const hasReadyCompany = readyCompanyCodes.length > 0;
    const effectiveSelection = selectedCompanyCodes ?? new Set(readyCompanyCodes);

    const toggleCompany = (companyCode: string) => {
      const base = selectedCompanyCodes ?? new Set(readyCompanyCodes);
      const next = new Set(base);
      if (next.has(companyCode)) {
        next.delete(companyCode);
      } else {
        next.add(companyCode);
      }
      setSelectedCompanyCodes(next);
    };

    const handleConfirm = () => {
      const authConfig = {
        type: 'OAUTH_CLIENT_CREDENTIALS' as const,
        tokenUrl: credentials.authTokenUrl,
        clientId: credentials.authClientId,
        clientSecret: credentials.clientSecret,
        scope: credentials.authScope,
      };

      const requests: CreateCompanyWithConnectionRequest[] = companies
        .filter((company) => company.companyCode && effectiveSelection.has(company.companyCode))
        .map((company) => ({
          name: company.companyName as string,
          countryCode: company.countryCode,
          address: {
            addressLine1: company.addressLine1,
            city: company.city,
            postCode: company.postCode,
            countryCode: company.countryCode,
          },
          provider: 'CEZANNE',
          protocol: 'REST',
          integrationTypes: ['HRIS_SYNC'],
          baseUrl: credentials.baseUrl,
          config: { externalCompanyCode: company.companyCode as string },
          authConfig,
        }));

      confirmSync(requests, {
        onSuccess: (companiesResult) => {
          // The confirm call succeeding means at least the attempt has committed - some rows may still have
          // failed, but there is nothing left to "cancel" from this point on, only follow-ups to wait out.
          onConfirmed();

          const meta: PayCalendarRequestMeta[] = [];
          const payCalendarRequests: CreateExternalPayCalendarRequest[] = [];

          companiesResult.results
            .filter((row) => row.result)
            .forEach((row) => {
              // Type guard already ensured by the filter above, but the discriminated union needs it spelled
              // out again here for TypeScript to narrow `row.result` as non-optional inside this closure.
              if (!row.result) {
                return;
              }
              const { company } = row.result;
              // Cezanne's sync data can only tell IRELAND apart from the UK - it has no signal for which of
              // the three UK-internal locations (England/Wales, Scotland, NI) a company is in, so that split
              // defaults to ENGLAND_WALES. Correctable afterwards via the existing pay-calendar edit UI.
              const location = company.countryCode === 'IE' ? 'IRELAND' : 'ENGLAND_WALES';
              const currency = company.countryCode === 'IE' ? 'EUR' : 'GBP';

              paySchedules.forEach((schedule) => {
                const scheduleLabel = schedule.title ?? schedule.payPeriodCode;
                meta.push({ companyCode: row.id, companyName: company.name, scheduleLabel });
                payCalendarRequests.push({
                  name: scheduleLabel,
                  companyId: company.id,
                  location,
                  currency,
                  workingDays: DEFAULT_WORKING_DAYS,
                  payType: 'IN_PERIOD',
                  accrualPeriod: 'PAY_CYCLE_TO_PAY_CYCLE',
                  fullPeriodStartingBalance: true,
                  externalPayCalendarId: schedule.payScheduleGuid ?? schedule.payPeriodCode,
                  ...resolveCutoff(schedule),
                  payPeriods: schedule.periods.map((period) => ({
                    startDate: period.startDate,
                    endDate: period.endDate,
                    payDate: period.contractualPayDate,
                  })),
                });
              });
            });

          if (payCalendarRequests.length > 0) {
            payCalendarRequestMetaRef.current = meta;
            createExternalPayCalendars(payCalendarRequests);
          }

          if (orgUnitGroupPaths.length === 0) {
            return;
          }

          const groupEntries: ResolveGroupPathsForCompanyRequest[] = companiesResult.results
            .filter((row) => row.result)
            .map((row) => ({
              companyCode: row.id,
              // Type guard already ensured by the filter above, but the discriminated union needs it spelled
              // out again here for TypeScript to narrow `row.result` as non-optional.
              companyId: (row.result as NonNullable<typeof row.result>).company.id,
              requests: orgUnitGroupPaths,
            }));

          if (groupEntries.length > 0) {
            resolveGroupPaths(groupEntries);
          }
        },
      });
    };

    if (confirmResult) {
      const companyNameByCode = new Map(
        companies
          .filter((company) => company.companyCode)
          .map((company) => [company.companyCode as string, company.companyName])
      );

      // Zips the pay-calendar bulk response (order-preserving) back to the company it belongs to. Pay-calendar
      // failures are surfaced per company here but never flip that company's own row to "Failed" below - the
      // company itself was already created and is not rolled back.
      const payCalendarStatusByCompanyCode = new Map<
        string,
        { createdCount: number; failedCount: number; failures: string[] }
      >();
      payCalendarsResult?.results.forEach((row, index) => {
        const meta = payCalendarRequestMetaRef.current[index];
        if (!meta) {
          return;
        }
        const status = payCalendarStatusByCompanyCode.get(meta.companyCode) ?? {
          createdCount: 0,
          failedCount: 0,
          failures: [],
        };
        if (row.result) {
          status.createdCount += 1;
        } else {
          status.failedCount += 1;
          status.failures.push(`${meta.scheduleLabel}: ${row.error?.message ?? 'Unknown error'}`);
        }
        payCalendarStatusByCompanyCode.set(meta.companyCode, status);
      });

      // Zips the group-path bulk response (already tagged per company - one call per company, unlike pay
      // calendars) back to the company it belongs to. Group failures are surfaced per company here but never
      // flip that company's own row to "Failed" below - the company itself was already created and is not
      // rolled back.
      const groupStatusByCompanyCode = new Map<
        string,
        { createdCount: number; failedCount: number; failures: string[] }
      >();
      groupPathsResult?.forEach(({ companyCode, result: groupResult }) => {
        const failedRows = groupResult.results.filter((row) => !row.result);
        groupStatusByCompanyCode.set(companyCode, {
          createdCount: groupResult.results.length - failedRows.length,
          failedCount: failedRows.length,
          failures: failedRows.map((row) => `${row.id}: ${row.error?.message ?? 'Unknown error'}`),
        });
      });

      const totalCount = confirmResult.results.length;
      const successCount = confirmResult.results.filter((row) => row.result).length;
      const failCount = totalCount - successCount;
      const allFailed = successCount === 0;

      let heading = 'Company creation complete';
      if (allFailed) {
        heading = 'Company creation failed';
      } else if (failCount > 0) {
        heading = 'Company creation complete, with errors';
      }

      return (
        <Stack spacing="20px">
          <Stack spacing="4px">
            <Heading as="h6" size="sm" color={allFailed ? 'system.error' : undefined}>
              {heading}
            </Heading>
            <Text textStyle="extraSmall" color="brand.darkGray">
              {allFailed
                ? `${totalCount} ${totalCount === 1 ? 'company' : 'companies'} failed to create`
                : `${successCount} of ${totalCount} ${
                    totalCount === 1 ? 'company' : 'companies'
                  } created${failCount > 0 ? `, ${failCount} failed` : ''}`}
            </Text>
            {isCreatingPayCalendars && (
              <Text textStyle="extraSmall" color="brand.darkGray">
                Creating pay calendars...
              </Text>
            )}
            {isCreatePayCalendarsError && (
              <Text textStyle="extraSmall" color="system.error">
                Failed to create pay calendars for the companies above. The companies were still
                created - try creating their pay calendars again from the pay calendar screen.
              </Text>
            )}
            {isResolvingGroupPaths && (
              <Text textStyle="extraSmall" color="brand.darkGray">
                Creating groups...
              </Text>
            )}
          </Stack>
          <Stack spacing="12px">
            {confirmResult.results.map((row) => {
              const payCalendarStatus = payCalendarStatusByCompanyCode.get(row.id);
              const groupStatus = groupStatusByCompanyCode.get(row.id);
              return (
                <Box
                  key={row.id}
                  borderWidth="1px"
                  borderColor="brand.lightGray1"
                  borderRadius="8px"
                  p="16px"
                >
                  <HStack justifyContent="space-between">
                    <Text fontWeight="bold">
                      {row.result?.company.name ?? companyNameByCode.get(row.id) ?? row.id}
                    </Text>
                    <Badge
                      colorScheme={row.result ? 'green' : 'red'}
                      fontSize="md"
                      px="14px"
                      py="4px"
                    >
                      {row.result ? 'Created' : 'Failed'}
                    </Badge>
                  </HStack>
                  {row.error && (
                    <Text textStyle="extraSmall" color="brand.black" mt="8px">
                      {row.error.message}
                    </Text>
                  )}
                  {payCalendarStatus && payCalendarStatus.failedCount > 0 && (
                    <Box mt="8px">
                      <Badge colorScheme="yellow" fontSize="xs">
                        {payCalendarStatus.failedCount} of{' '}
                        {payCalendarStatus.createdCount + payCalendarStatus.failedCount} pay{' '}
                        {payCalendarStatus.createdCount + payCalendarStatus.failedCount === 1
                          ? 'calendar'
                          : 'calendars'}{' '}
                        failed
                      </Badge>
                      <Stack spacing="2px" mt="4px">
                        {payCalendarStatus.failures.map((failure) => (
                          <Text key={failure} textStyle="extraSmall" color="brand.black">
                            {failure}
                          </Text>
                        ))}
                      </Stack>
                    </Box>
                  )}
                  {groupStatus && groupStatus.failedCount > 0 && (
                    <Box mt="8px">
                      <Badge colorScheme="yellow" fontSize="xs">
                        {groupStatus.failedCount} of{' '}
                        {groupStatus.createdCount + groupStatus.failedCount}{' '}
                        {groupStatus.createdCount + groupStatus.failedCount === 1
                          ? 'group'
                          : 'groups'}{' '}
                        failed
                      </Badge>
                      <Stack spacing="2px" mt="4px">
                        {groupStatus.failures.map((failure) => (
                          <Text key={failure} textStyle="extraSmall" color="brand.black">
                            {failure}
                          </Text>
                        ))}
                      </Stack>
                    </Box>
                  )}
                </Box>
              );
            })}
          </Stack>
        </Stack>
      );
    }

    return (
      <Stack spacing="24px">
        <Box>
          <Heading as="h6" size="sm" mb="12px">
            Companies found
          </Heading>
          <Stack spacing="16px">
            {companies.map((company) => {
              const isReady = company.missingMandatoryFields.length === 0;
              return (
                <Box
                  key={company.companyCode}
                  borderWidth="1px"
                  borderColor="brand.lightGray1"
                  borderRadius="8px"
                  p="16px"
                >
                  <HStack justifyContent="space-between">
                    <HStack>
                      {isReady && company.companyCode && (
                        <Checkbox
                          isChecked={effectiveSelection.has(company.companyCode)}
                          onChange={() => toggleCompany(company.companyCode as string)}
                          aria-label={`Include ${company.companyName ?? company.companyCode}`}
                        />
                      )}
                      <Text fontWeight="bold">{company.companyName ?? company.companyCode}</Text>
                    </HStack>
                    <HStack>
                      {company.alreadyConnected && (
                        <Badge colorScheme="yellow">Already connected</Badge>
                      )}
                      <Badge colorScheme={isReady ? 'green' : 'red'}>
                        {isReady ? 'Ready to create' : 'Blocked'}
                      </Badge>
                    </HStack>
                  </HStack>
                  <Text textStyle="extraSmall" color="brand.darkGray">
                    {company.legalCompanyName} - {company.country} - {company.addressLine1},{' '}
                    {company.city} {company.postCode}
                  </Text>
                  {company.missingMandatoryFields.length > 0 && (
                    <Text textStyle="extraSmall" color="system.error" mt="4px">
                      Missing (blocking): {company.missingMandatoryFields.join(', ')}
                    </Text>
                  )}
                  {company.missingOptionalFields.length > 0 && (
                    <Text textStyle="extraSmall" color="brand.darkGray" mt="4px">
                      Missing (optional): {company.missingOptionalFields.join(', ')}
                    </Text>
                  )}
                </Box>
              );
            })}
          </Stack>
        </Box>

        <Divider />

        <Box>
          <Heading as="h6" size="sm" mb="12px">
            Pay schedules
          </Heading>
          <List spacing="6px">
            {paySchedules.map((schedule) => (
              <ListItem key={schedule.payPeriodCode}>
                {schedule.title ?? schedule.payPeriodCode} - {schedule.periods.length} upcoming
                period(s)
              </ListItem>
            ))}
            {paySchedules.length === 0 && <Text color="brand.darkGray">None found.</Text>}
          </List>
        </Box>

        <Divider />

        <Box>
          <Heading as="h6" size="sm" mb="12px">
            Org units
          </Heading>
          <List spacing="6px">
            {orgUnits.map((orgUnit) => (
              <ListItem key={orgUnit.code}>
                {orgUnit.name ?? orgUnit.code}
                {orgUnit.parentCode && (
                  <Text as="span" color="brand.darkGray">
                    {' '}
                    (parent: {orgUnit.parentCode})
                  </Text>
                )}
              </ListItem>
            ))}
            {orgUnits.length === 0 && <Text color="brand.darkGray">None found.</Text>}
          </List>
        </Box>

        <Portal containerRef={ref as RefObject<HTMLElement>}>
          <Button size="sm" variant="outline" onClick={onStartOver} mr="12px">
            Start over
          </Button>
          <Button
            size="sm"
            isDisabled={effectiveSelection.size === 0 || isConfirming}
            isLoading={isConfirming}
            onClick={handleConfirm}
          >
            Confirm &amp; create
          </Button>
        </Portal>
        {!hasReadyCompany && (
          <Text textStyle="extraSmall" color="system.error">
            No company in this tenant is ready to create yet.
          </Text>
        )}
        {hasReadyCompany && effectiveSelection.size === 0 && (
          <Text textStyle="extraSmall" color="system.error">
            Select at least one company to create.
          </Text>
        )}
        {isConfirmError && (
          <Text textStyle="extraSmall" color="system.error">
            Failed to confirm the sync. Try again.
          </Text>
        )}
      </Stack>
    );
  }
);

CezanneSyncReview.displayName = 'CezanneSyncReview';

export default CezanneSyncReview;
